/**
 * «Toca pagar»: which months of the fixed expenses are still to be recorded, and when each falls due
 * (the owner, 2026-10-07: «we need a feature where we can register repeating expenses (rent,
 * internet, etc.)»).
 *
 * A fixed expense is a setting (`gastosFijos`, in `@abastece/shared`); a month of it is recorded
 * when an expense stands with that month's id (`idDeOcurrencia`), or a correction of it does. One
 * that was voided outright did not happen, and is asked for again, under the next try's id: the
 * books refuse an id they have, voided or not. Pure, but for `leerPendientes`, so the screen, the
 * home screen and the tests share one answer.
 */

import { idDeOcurrencia, sumarDias, type GastoFijo } from '@abastece/shared';
import type { Gasto } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { anulacionQueQuedo } from './gastos.js';
import { mesDe, otroMes } from './historial.js';
import { leerSinEfecto, tuvoEfecto } from './sinEfecto.js';

/** How far ahead «Toca pagar» shows what is coming: a week, time to have the money ready. */
export const DIAS_ANTES = 7;

/** How often one can repeat, as the form offers it. */
export const CADA_MESES = [1, 2, 3, 6, 12] as const;

/** How many times a month is tried before it is left alone: each void takes one. */
export const VECES = 10;

/** The day a month of it falls due: its day, or that month's last day in a shorter month. */
export function venceEl(fijo: Pick<GastoFijo, 'dia'>, periodo: string): string {
  const [a, m] = periodo.split('-').map(Number) as [number, number];
  const ultimo = new Date(Date.UTC(a, m, 0)).getUTCDate();
  return `${periodo}-${String(Math.min(fijo.dia, ultimo)).padStart(2, '0')}`;
}

/** The months it falls due, from its first one, up to and including `hasta`. */
export function periodosHasta(fijo: Pick<GastoFijo, 'desde' | 'cadaMeses'>, hasta: string): string[] {
  const periodos: string[] = [];
  for (let p = fijo.desde; p <= hasta && periodos.length < 600; p = otroMes(p, fijo.cadaMeses)) periodos.push(p);
  return periodos;
}

/** Where a new one starts counting: this month if its day has not gone by yet, else the next. */
export function primerPeriodo(dia: number, hoy: string): string {
  const este = mesDe(hoy);
  return venceEl({ dia }, este) >= hoy ? este : otroMes(este, 1);
}

export type EstadoOcurrencia = 'vencido' | 'hoy' | 'proximo';

/** A month of a fixed expense still to be recorded. */
export interface Ocurrencia {
  fijo: GastoFijo;
  periodo: string;
  vence: string;
  estado: EstadoOcurrencia;
  /** The id its expense takes, the same on every phone: the first try's, or the next after a void. */
  gastoId: string;
}

/** What the books hold under an id: nothing, an expense that stands (or its correction does), or one voided. */
export type EstadoDeId = 'libre' | 'registrado' | 'anulado';

/**
 * The months not yet recorded, nor passed over, that fell due already or fall due within
 * `diasAntes` days: the ones gone by first. Each month's tries are walked in order: one that stands
 * records it, and the first id nobody has is what its expense takes.
 */
export function pendientes(
  fijos: readonly GastoFijo[],
  hoy: string,
  estadoDe: (gastoId: string) => EstadoDeId,
  diasAntes: number = DIAS_ANTES,
): Ocurrencia[] {
  const hasta = sumarDias(hoy, diasAntes);
  const lista: Ocurrencia[] = [];
  for (const fijo of fijos) {
    if (!fijo.activo) continue;
    for (const periodo of periodosHasta(fijo, mesDe(hasta))) {
      const vence = venceEl(fijo, periodo);
      if (vence > hasta || fijo.omitidos.includes(periodo)) continue;
      let gastoId: string | null = null;
      for (let vez = 1; vez <= VECES && gastoId === null; vez++) {
        const id = idDeOcurrencia(fijo.id, periodo, vez);
        const estado = estadoDe(id);
        if (estado === 'registrado') break;
        if (estado === 'libre') gastoId = id;
      }
      if (gastoId === null) continue;
      lista.push({ fijo, periodo, vence, gastoId, estado: vence < hoy ? 'vencido' : vence === hoy ? 'hoy' : 'proximo' });
    }
  }
  return lista.sort((a, b) => a.vence.localeCompare(b.vence) || a.fijo.descripcion.localeCompare(b.fijo.descripcion, 'es'));
}

/**
 * Each fixed expense once, in the order the first of its months fell due, with how many months of
 * it are waiting: the home screen names the rent once, not once a month.
 */
export function porFijo(ocurrencias: readonly Ocurrencia[]): { fijo: GastoFijo; meses: number }[] {
  const grupos = new Map<string, { fijo: GastoFijo; meses: number }>();
  for (const o of ocurrencias) {
    const grupo = grupos.get(o.fijo.id);
    if (grupo) grupo.meses++;
    else grupos.set(o.fijo.id, { fijo: o.fijo, meses: 1 });
  }
  return [...grupos.values()];
}

/** Each expense's voids, oldest first, as «Gastos» reads them: one that named what replaced it was a correction. */
export type Anulaciones = ReadonlyMap<string, readonly { motivo: string; reemplazo?: string }[]>;

/**
 * Whether a month is recorded: its expense stands, or the correction that replaced it does —
 * followed as far as corrections of corrections go.
 */
export function estaRegistrada(gastoId: string, buscar: (id: string) => Gasto | undefined, anulaciones: Anulaciones): boolean {
  let gasto = buscar(gastoId);
  for (let pasos = 0; gasto?.anulado && pasos < 20; pasos++) {
    const reemplazo = anulacionQueQuedo(gasto, anulaciones.get(gasto.id) ?? [])?.reemplazo;
    gasto = reemplazo ? buscar(reemplazo) : undefined;
  }
  return Boolean(gasto && !gasto.anulado);
}

/** What the books hold under an id, for `pendientes`. */
export function estadoDeId(gastoId: string, buscar: (id: string) => Gasto | undefined, anulaciones: Anulaciones): EstadoDeId {
  if (!buscar(gastoId)) return 'libre';
  return estaRegistrada(gastoId, buscar, anulaciones) ? 'registrado' : 'anulado';
}

/** The voids of expenses, from the log: what «Gastos» lists and what a correction replaced. */
export async function leerAnulaciones(): Promise<Map<string, { motivo: string; reemplazo?: string }[]>> {
  const porGasto = new Map<string, { motivo: string; reemplazo?: string }[]>();
  const sinEfecto = await leerSinEfecto();
  const eventos = (await db.eventos.where('type').equals('gasto.anulado').toArray())
    .filter((e) => tuvoEfecto(e, sinEfecto))
    .sort((a, b) => a.clientTs.localeCompare(b.clientTs));
  for (const e of eventos) {
    const p = e.payload as { gastoId: string; motivo: string; reemplazo?: string };
    porGasto.set(p.gastoId, [...(porGasto.get(p.gastoId) ?? []), p]);
  }
  return porGasto;
}

/**
 * The months still to be recorded, read from the phone's books: for the home screen, which has
 * none of the expenses loaded, and «Análisis» → «Dinero», which looks `diasAntes` ahead. Only the
 * expenses those months' tries would be are read, and the log only when one of them was voided.
 */
export async function leerPendientes(hoy: string, diasAntes: number = DIAS_ANTES): Promise<Ocurrencia[]> {
  const { gastosFijos } = await readSettings();
  if (gastosFijos.length === 0) return [];
  const ids = pendientes(gastosFijos, hoy, () => 'libre', diasAntes).flatMap((c) =>
    Array.from({ length: VECES }, (_, i) => idDeOcurrencia(c.fijo.id, c.periodo, i + 1)),
  );
  const leidos = new Map<string, Gasto>();
  for (const g of await db.gastos.bulkGet(ids)) if (g) leidos.set(g.id, g);
  const buscar = (id: string) => leidos.get(id);
  if (![...leidos.values()].some((g) => g.anulado)) return pendientes(gastosFijos, hoy, (id) => (leidos.has(id) ? 'registrado' : 'libre'), diasAntes);
  // A month voided: follow its corrections, reading each replacement as the chain reaches it.
  const anulaciones = await leerAnulaciones();
  const reemplazos = [...anulaciones.values()].flatMap((l) => l.map((a) => a.reemplazo).filter((r): r is string => Boolean(r)));
  for (const g of await db.gastos.bulkGet(reemplazos)) if (g) leidos.set(g.id, g);
  return pendientes(gastosFijos, hoy, (id) => estadoDeId(id, buscar, anulaciones), diasAntes);
}
